import { useState } from "react";
import { AlertCircle, X } from "lucide-react";
import ReusableDropdown from "../../../components/ReusableDropdown";
import { ESCALATION_TYPES } from "../constants/translations";
import { getTypeLabel } from "../utils/helpers";

/**
 * Modal for creating a new escalation.
 *
 * Props:
 *   onClose    {function}
 *   onSubmit   {function}  — called with { type, description }
 *   loading    {boolean}
 *   theme      {"light"|"dark"}
 *   screenSize {string}
 *   styles     {object}    — from useStyles
 *   language   {"en"|"ar"}
 *   t          {object}    — text[language]
 */
const CreateEscalationModal = ({
  onClose,
  onSubmit,
  loading,
  theme,
  screenSize,
  styles,
  language,
  t,
}) => {
  const [type, setType] = useState("");
  const [description, setDescription] = useState("");
  const [errors, setErrors] = useState({});

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = {};
    if (!type) errs.type = t.escalationType + " " + t.selectType.toLowerCase();
    if (!description)
      errs.description =
        t.description + " " + t.descriptionRequired.toLowerCase();

    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    onSubmit({ type, description });
  };

  const handleModalClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleFormKeyDown = (e) => {
    if (e.key !== "Enter") return;
    const tag = (e.target?.tagName || "").toLowerCase();
    if (tag === "button" || e.target.type === "submit") return;
    e.preventDefault();
  };

  return (
    <div style={styles.modal} onClick={handleModalClick}>
      <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>{t.createEscalationTitle}</h2>
          <button style={styles.closeBtn} onClick={onClose} title={t.close}>
            <X size={screenSize === "xs" ? 20 : 24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} onKeyDown={handleFormKeyDown}>
          <div style={styles.sectionTitle}>{t.escalationDetails}</div>

          {/* Type */}
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>
              {t.escalationType}
              <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <ReusableDropdown
              items={ESCALATION_TYPES.map((typeVal) => ({
                id: typeVal,
                label: getTypeLabel(typeVal, language),
              }))}
              value={type}
              onChange={(val) => {
                setType(val);
                if (errors.type) setErrors({ ...errors, type: "" });
              }}
              placeholder={t.selectType}
              theme={theme}
              language={language}
              getItemKey={(item) => item.id}
              getItemLabel={(item) => item.label}
              showAllOnArrowClick
              showClear={type !== ""}
            />
            {errors.type && (
              <span style={styles.errorText}>
                <AlertCircle
                  size={14}
                  style={{ display: "inline", marginRight: "0.25rem" }}
                />
                {errors.type}
              </span>
            )}
          </div>

          {/* Description */}
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>
              {t.description}
              <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                if (errors.description)
                  setErrors({ ...errors, description: "" });
              }}
              style={{
                ...styles.formTextarea,
                borderColor: errors.description ? "#ef4444" : undefined,
              }}
              placeholder={t.describeIssue}
            />
            {errors.description && (
              <span style={styles.errorText}>
                <AlertCircle
                  size={14}
                  style={{ display: "inline", marginRight: "0.25rem" }}
                />
                {errors.description}
              </span>
            )}
          </div>

          {/* Actions */}
          <div style={styles.modalActions}>
            <button
              type="button"
              onClick={onClose}
              style={{
                ...styles.cancelBtn,
                minWidth: screenSize === "xs" ? "100%" : "150px",
              }}
            >
              {t.cancel}
            </button>
            <button
              type="submit"
              style={{
                ...styles.submitBtn,
                minWidth: screenSize === "xs" ? "100%" : "150px",
              }}
              disabled={loading}
            >
              {loading ? t.submitting : t.submit}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateEscalationModal;